import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, formatMoney, STATUS_LABEL, STATUS_BADGE } from "@/lib/api";
import {
  ArrowLeft, Upload, FileText, Trash2, Pencil, Heart, Phone, MapPin, ShieldCheck,
  AlertTriangle, History, Activity, Stethoscope, FileSignature, Pill, Image as ImageIcon,
  ClipboardList, Receipt, FileCheck, Info, FileDown, Braces,
} from "lucide-react";
import { toast } from "sonner";
import PatientFormFields, { EMPTY_PATIENT } from "@/components/PatientFormFields";
import Odontograma from "@/components/Odontograma";
import TreatmentPlan from "@/components/TreatmentPlan";
import Budgets from "@/components/Budgets";
import AnamnesisForm from "@/components/AnamnesisForm";
import EvolucionesList from "@/components/EvolucionesList";
import Periodontograma from "@/components/Periodontograma";
import RecetasList from "@/components/RecetasList";
import ClinicalDocsList from "@/components/ClinicalDocsList";
import PatientReceiptsList from "@/components/PatientReceiptsList";
import OrthoTab from "@/components/ortho/OrthoTab";
import { generatePatientPdf } from "@/lib/patientPdf";
import { useBranding } from "@/context/BrandingContext";

const TABS = [
  { key: "info", label: "Información", icon: Info },
  { key: "historial", label: "Historial", icon: History },
  { key: "evoluciones", label: "Evoluciones", icon: Activity },
  { key: "anamnesis", label: "Ficha Anamnesis", icon: Stethoscope },
  { key: "odontograma", label: "Odontograma", icon: ClipboardList },
  { key: "periodontograma", label: "Periodontograma", icon: ClipboardList },
  { key: "plan", label: "Plan de tratamiento", icon: ClipboardList },
  { key: "ortodoncia", label: "Ortodoncia", icon: Braces },
  { key: "presupuestos", label: "Presupuestos", icon: Receipt },
  { key: "recibos", label: "Recibos", icon: FileCheck },
  { key: "rx", label: "Rx y Documentos", icon: ImageIcon },
  { key: "recetas", label: "Recetas", icon: Pill },
  { key: "doc-clinicos", label: "Documentos Clínicos", icon: FileText },
  { key: "consentimientos", label: "Consentimientos", icon: FileSignature },
];

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="label-eyebrow">{label}</div>
        <div className="text-sm mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}

function Tag({ children, tone = "muted" }) {
  const tones = {
    muted: "bg-muted text-foreground",
    primary: "bg-primary/15 text-primary",
    accent: "bg-accent/20 text-accent",
    danger: "bg-destructive/15 text-destructive",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm ${tones[tone]}`}>
      {children}
    </span>
  );
}

export default function PacienteDetalle() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(EMPTY_PATIENT);
  const [tab, setTab] = useState("historial");
  const [treatments, setTreatments] = useState([]);
  const [planItems, setPlanItems] = useState([]);
  const fileRef = useRef(null);
  const { branding } = useBranding();
  const [downloading, setDownloading] = useState(false);

  const downloadPatientPdf = async () => {
    if (!data?.patient) return;
    setDownloading(true);
    try {
      const [evRes, anamRes, planRes, budRes, recRes] = await Promise.all([
        api.get(`/patients/${id}/evolutions`).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/anamnesis`).catch(() => ({ data: null })),
        api.get(`/patients/${id}/plan`).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/budgets`).catch(() => ({ data: [] })),
        api.get(`/patients/${id}/receipts`).catch(() => ({ data: [] })),
      ]);
      generatePatientPdf({
        patient: data.patient,
        data,
        evolutions: evRes.data || [],
        anamnesis: anamRes.data || null,
        plan: planRes.data || [],
        budgets: budRes.data || [],
        receipts: recRes.data || [],
        branding,
      });
      toast.success("PDF generado");
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  const load = async () => {
    const { data } = await api.get(`/patients/${id}`);
    setData(data);
    setForm({ ...EMPTY_PATIENT, ...data.patient });
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  useEffect(() => {
    api.get("/treatments").then((r) => setTreatments(r.data)).catch(() => {});
  }, []);

  const refreshPlan = async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/patients/${id}/plan`);
      setPlanItems(data);
    } catch { /* noop */ }
  };
  useEffect(() => { refreshPlan(); }, [id]); // eslint-disable-line

  const save = async () => {
    try {
      await api.put(`/patients/${id}`, form);
      toast.success("Actualizado");
      setEdit(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/patients/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Archivo subido");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeFile = async (fid) => {
    if (!window.confirm("¿Eliminar archivo?")) return;
    try { await api.delete(`/files/${fid}`); load(); } catch { toast.error("No se pudo eliminar"); }
  };

  if (!data) return <div className="text-sm text-muted-foreground">Cargando…</div>;
  const p = data.patient;
  const token = localStorage.getItem("df_token");
  const fullDoc = p.document_number ? `${p.document_type || ""} ${p.document_number}` : "";
  const fullEmergency = p.emergency_contact_name
    ? `${p.emergency_contact_name}${p.emergency_contact_phone ? " · " + p.emergency_contact_phone : ""}`
    : "";
  const hasMedicalAlert = p.allergies || p.chronic_conditions || p.pregnant || p.current_medications;

  return (
    <div className="space-y-5" data-testid="patient-detail">
      <Link to="/pacientes" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
        <ArrowLeft className="w-4 h-4" /> Pacientes
      </Link>

      {/* Header card sticky con info del paciente */}
      <header className="card-flat p-5 flex items-start justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="label-eyebrow mb-1">Ficha clínica</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-none">{p.name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {fullDoc && <Tag>{fullDoc}</Tag>}
            {p.eps && <Tag tone="primary">EPS · {p.eps}</Tag>}
            {p.regimen && <Tag>{p.regimen}</Tag>}
            {p.blood_type && <Tag tone="accent">RH {p.blood_type}</Tag>}
            {p.pregnant && <Tag tone="accent">Embarazo</Tag>}
            {p.smoker && <Tag>Fumador(a)</Tag>}
            {p.phone && <Tag><Phone className="w-2.5 h-2.5 inline mr-1" />{p.phone}</Tag>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={downloadPatientPdf}
            disabled={downloading}
            className="px-4 h-10 rounded-sm border border-border bg-card hover:bg-muted text-sm font-bold uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="download-patient-pdf"
          >
            <FileDown className="w-4 h-4" /> {downloading ? "Generando…" : "Descargar PDF"}
          </button>
          <button onClick={() => setEdit((v) => !v)} className="btn-primary" data-testid="edit-patient-toggle">
            <Pencil className="w-4 h-4" /> {edit ? "Cancelar" : "Editar info"}
          </button>
        </div>
      </header>

      {hasMedicalAlert && !edit && (
        <div className="card-flat p-4 border-l-4 border-l-destructive flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-destructive mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-bold mb-1">Alertas médicas</div>
            <div className="space-y-0.5 text-muted-foreground">
              {p.allergies && <div><span className="font-semibold text-foreground">Alergias:</span> {p.allergies}</div>}
              {p.chronic_conditions && <div><span className="font-semibold text-foreground">Crónicas:</span> {p.chronic_conditions}</div>}
              {p.current_medications && <div><span className="font-semibold text-foreground">Medicación:</span> {p.current_medications}</div>}
              {p.pregnant && <div><span className="font-semibold text-foreground">Embarazo activo</span></div>}
            </div>
          </div>
        </div>
      )}

      {edit ? (
        <div className="space-y-4">
          <PatientFormFields form={form} setForm={setForm} />
          <div className="flex gap-2">
            <button onClick={save} className="btn-primary" data-testid="patient-detail-save">Guardar cambios</button>
            <button onClick={() => { setEdit(false); setForm({ ...EMPTY_PATIENT, ...p }); }} className="px-4 h-10 rounded-sm border border-border hover:bg-muted text-sm">Cancelar</button>
          </div>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex border-b border-border overflow-x-auto sticky top-16 bg-background z-20" data-testid="patient-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`px-3 sm:px-4 h-11 text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                    active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}>
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>

          {tab === "info" && (
            <div className="card-flat p-5 space-y-1">
              <h2 className="font-display font-bold text-xl tracking-tight mb-2">Información personal</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                <InfoRow icon={Phone} label="Teléfono" value={p.phone} />
                <InfoRow icon={Phone} label="Correo" value={p.email} />
                <InfoRow icon={MapPin} label="Dirección" value={p.address} />
                <InfoRow icon={MapPin} label="Ciudad" value={p.city} />
                <InfoRow icon={Heart} label="Fecha nacimiento" value={p.birth_date} />
                <InfoRow icon={Heart} label="Género" value={p.gender === "F" ? "Femenino" : p.gender === "M" ? "Masculino" : p.gender === "O" ? "Otro" : ""} />
                <InfoRow icon={Heart} label="Ocupación" value={p.occupation} />
                <InfoRow icon={Phone} label="Contacto emergencia" value={fullEmergency} />
              </div>
              <h2 className="font-display font-bold text-xl tracking-tight mt-4 mb-2">Antecedentes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                <InfoRow icon={ShieldCheck} label="Alergias" value={p.allergies} />
                <InfoRow icon={ShieldCheck} label="Crónicas" value={p.chronic_conditions} />
                <InfoRow icon={ShieldCheck} label="Medicamentos" value={p.current_medications} />
                <InfoRow icon={ShieldCheck} label="Cirugías previas" value={p.previous_surgeries} />
                <InfoRow icon={Heart} label="Última visita" value={p.last_dental_visit} />
                <InfoRow icon={Heart} label="Cepillado" value={p.brushing_frequency} />
              </div>
              {(p.dental_history || p.notes) && (
                <>
                  <h2 className="font-display font-bold text-xl tracking-tight mt-4 mb-2">Notas</h2>
                  {p.dental_history && (
                    <div className="py-2">
                      <div className="label-eyebrow">Historia odontológica</div>
                      <div className="text-sm mt-1 whitespace-pre-wrap">{p.dental_history}</div>
                    </div>
                  )}
                  {p.notes && (
                    <div className="py-2">
                      <div className="label-eyebrow">Notas clínicas</div>
                      <div className="text-sm mt-1 whitespace-pre-wrap">{p.notes}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === "historial" && (
            <section className="card-flat p-5">
              <h2 className="font-display font-bold text-2xl tracking-tight mb-1">Historial de citas</h2>
              <p className="text-sm text-muted-foreground mb-4">Cronología completa de visitas del paciente.</p>
              <div className="space-y-2">
                {(data.appointments || []).length === 0 && (
                  <div className="text-sm text-muted-foreground py-12 text-center">No hay citas registradas.</div>
                )}
                {(data.appointments || []).map((a) => (
                  <div key={a.id} className={`p-3 rounded-sm bg-card border border-border stripe-${a.status} grid grid-cols-12 gap-2 items-center`}>
                    <div className="col-span-3 font-mono text-xs text-muted-foreground">
                      {new Date(a.start).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                    <div className="col-span-4 text-sm font-semibold truncate">{a.treatment_name || "Cita"}</div>
                    <div className="col-span-3 text-xs">
                      <span className={`uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm text-[10px] ${STATUS_BADGE[a.status] || STATUS_BADGE.pendiente}`}>
                        {STATUS_LABEL[a.status]}
                      </span>
                    </div>
                    <div className="col-span-2 text-right font-mono text-sm">{formatMoney(a.price)}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {tab === "evoluciones" && <EvolucionesList patientId={id} />}
          {tab === "anamnesis" && <AnamnesisForm patientId={id} />}
          {tab === "odontograma" && <Odontograma patientId={id} />}
          {tab === "periodontograma" && <Periodontograma patientId={id} />}
          {tab === "plan" && <TreatmentPlan patientId={id} treatments={treatments} onChange={refreshPlan} />}
          {tab === "ortodoncia" && <OrthoTab patientId={id} patient={p} />}
          {tab === "presupuestos" && <Budgets patient={p} planItems={planItems} />}

          {tab === "recibos" && <PatientReceiptsList patientId={id} />}

          {tab === "rx" && (
            <div className="space-y-4" data-testid="rx-tab">
              <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-display font-bold text-2xl tracking-tight">Rx y Documentos</h2>
                  <p className="text-sm text-muted-foreground">Radiografías, fotografías clínicas y archivos adjuntos del paciente.</p>
                </div>
                <button onClick={() => fileRef.current?.click()} className="btn-primary" data-testid="upload-file-btn">
                  <Upload className="w-4 h-4" /> Subir archivo
                </button>
                <input ref={fileRef} type="file" onChange={onUpload} className="hidden" accept="image/*,application/pdf" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(data.files || []).length === 0 && (
                  <div className="card-flat p-12 col-span-full text-center text-sm text-muted-foreground">Sin archivos cargados.</div>
                )}
                {(data.files || []).map((f) => {
                  const isImg = (f.content_type || "").startsWith("image/");
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/download${token ? `?auth=${token}` : ""}`;
                  return (
                    <div key={f.id} className="card-flat overflow-hidden">
                      {isImg ? (
                        <a href={url} target="_blank" rel="noreferrer" className="block">
                          <img src={url} alt={f.original_filename} className="w-full h-44 object-cover" />
                        </a>
                      ) : (
                        <div className="w-full h-44 bg-muted flex items-center justify-center">
                          <FileText className="w-10 h-10 text-muted-foreground" />
                        </div>
                      )}
                      <div className="p-3 flex items-center justify-between gap-2">
                        <a href={url} target="_blank" rel="noreferrer" className="text-sm font-semibold truncate hover:text-primary">
                          {f.original_filename}
                        </a>
                        <button onClick={() => removeFile(f.id)} className="p-1.5 text-muted-foreground hover:text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "recetas" && <RecetasList patientId={id} patient={p} />}
          {tab === "doc-clinicos" && <ClinicalDocsList patientId={id} patient={p} kind="doc" />}
          {tab === "consentimientos" && <ClinicalDocsList patientId={id} patient={p} kind="consent" />}
        </>
      )}
    </div>
  );
}
